package com.swetdot.swetgram.ui.profile

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.defaultMinSize
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextOverflow
import com.swetdot.swetgram.ui.text.resolve
import com.swetdot.swetgram.domain.BlockedUser
import com.swetdot.swetgram.domain.LocalDay
import com.swetdot.swetgram.domain.UserId
import com.swetdot.swetgram.ui.chat.render
import com.swetdot.swetgram.ui.design.Sizes
import com.swetdot.swetgram.ui.design.Space
import com.swetdot.swetgram.ui.design.SwetAvatar
import com.swetdot.swetgram.ui.design.SwetEmptyState
import com.swetdot.swetgram.ui.design.SwetErrorBanner
import com.swetdot.swetgram.ui.design.SwetListRowSkeleton
import com.swetdot.swetgram.ui.design.SwetTextButton
import com.swetdot.swetgram.ui.design.SwetTopAppBar
import com.swetdot.swetgram.ui.design.TouchTarget
import com.swetdot.swetgram.ui.theme.SwetgramThemeTokens
import kotlinx.datetime.TimeZone
import org.jetbrains.compose.resources.stringResource
import swetgram.shared.generated.resources.Res
import swetgram.shared.generated.resources.handle_prefixed
import swetgram.shared.generated.resources.profile_blocked_empty_body
import swetgram.shared.generated.resources.profile_blocked_empty_title
import swetgram.shared.generated.resources.profile_blocked_on
import swetgram.shared.generated.resources.profile_blocked_users_title
import swetgram.shared.generated.resources.profile_unblock
import swetgram.shared.generated.resources.profile_unblock_body
import swetgram.shared.generated.resources.profile_unblock_title

/**
 * Blocked users -- SPS §16.1's fourth privacy control.
 *
 * This is the ONLY place a block can be undone, which shapes two decisions here:
 *
 *  - A row survives a `blocked_at` the client cannot parse. `data/profile` keeps the entry with a
 *    null date rather than dropping it, and this screen simply omits the line -- losing the row
 *    would strand the block with no way to lift it.
 *  - Unblocking is confirmed (SDS §11.1). It is reversible, but it is a safety control in a list
 *    where every row looks the same, and an accidental tap silently re-opens a channel the user
 *    closed on purpose.
 *
 * DECISIONS §6: blocking does not delete the conversation, and the server answers a blocked
 * sender with a NEUTRAL error rather than saying they were blocked. Nothing on this screen tries
 * to soften or reveal either -- it lists blocks and lifts them, and that is all.
 */
@Composable
fun BlockedUsersScreen(
    state: BlockedUsersUiState,
    onUnblock: (UserId) -> Unit,
    onBack: () -> Unit,
    modifier: Modifier = Modifier,
    onRetry: (() -> Unit)? = null,
    onDismissError: (() -> Unit)? = null,
    /** Null disables pagination -- for a caller that shows a single page on purpose. */
    onLoadMore: (() -> Unit)? = null,
    zone: TimeZone = TimeZone.currentSystemDefault(),
) {
    var pendingUnblock by remember { mutableStateOf<BlockedUser?>(null) }

    Column(modifier.fillMaxSize()) {
        SwetTopAppBar(
            title = stringResource(Res.string.profile_blocked_users_title),
            onBack = onBack,
        )

        // Over the list, never in place of it: a failed page must not empty the rows that did
        // load, because those rows are the only way to unblock anyone.
        if (state.error != null) {
            SwetErrorBanner(
                message = state.error.resolve(),
                onRetry = onRetry,
                onDismiss = onDismissError,
            )
        }

        when {
            // First load. Skeleton rows rather than a spinner (SDS §2.1): the list lands where
            // the placeholders were instead of replacing a spinner and reflowing.
            state.items.isEmpty() && !state.loadedOnce -> Column(Modifier.fillMaxSize()) {
                repeat(SKELETON_ROWS) { SwetListRowSkeleton() }
            }

            // §17.1's voice: short, helpful, calm.
            state.items.isEmpty() -> SwetEmptyState(
                title = stringResource(Res.string.profile_blocked_empty_title),
                body = stringResource(Res.string.profile_blocked_empty_body),
            )

            // A pushed detail route, so no tab bar underneath carries the system-bar inset and
            // the last row would otherwise sit behind the gesture / navigation buttons.
            else -> LazyColumn(Modifier.fillMaxSize().navigationBarsPadding()) {
                items(
                    items = state.items,
                    // The user id, not the list position: unblocking removes a row, and a
                    // positional key would animate the wrong one out.
                    key = { it.user.id.value },
                ) { blocked ->
                    BlockedUserRow(
                        blocked = blocked,
                        zone = zone,
                        onUnblock = { pendingUnblock = blocked },
                    )
                }

                if (state.hasMore) {
                    item(key = PAGING_FOOTER_KEY) {
                        // Keyed on the item count, so it fires once per page rather than on
                        // every recomposition -- and does NOT fire again after a failed page,
                        // since the count did not change. The banner's Retry is the way back.
                        LaunchedEffect(state.items.size) { onLoadMore?.invoke() }
                        SwetListRowSkeleton()
                    }
                }
            }
        }
    }

    pendingUnblock?.let { blocked ->
        ProfileConfirmDialog(
            // THE NAME IS AN ARGUMENT, never pasted into the sentence in Kotlin. English can put a
            // bare name straight after "Unblock"; Russian would need it in the accusative, and the
            // name arrives in whatever case its owner typed. `profile_unblock_title` therefore
            // carries a noun of its own in the Russian file -- "Разблокировать пользователя X?" --
            // which takes the case and leaves the name in the nominative.
            title = stringResource(Res.string.profile_unblock_title, blocked.user.displayName),
            body = stringResource(Res.string.profile_unblock_body),
            confirmText = stringResource(Res.string.profile_unblock),
            onConfirm = {
                pendingUnblock = null
                onUnblock(blocked.user.id)
            },
            onDismiss = { pendingUnblock = null },
            // Reversible, and the safety-reducing direction -- so it is confirmed, but it is not
            // painted as a deletion.
            destructive = false,
        )
    }
}

/** Enough placeholder rows to fill the first screen without pretending to know the real count. */
private const val SKELETON_ROWS = 5
private const val PAGING_FOOTER_KEY = "blocked-users-paging-footer"

/**
 * One blocked account.
 *
 * Hand-built rather than `SwetListRow` for one reason: the row needs a trailing ACTION, and
 * §12.1's row reserves its trailing slot for a timestamp and an unread badge. Everything else --
 * the 72dp height, the 44dp avatar, the title and secondary line -- is taken from the same tokens
 * it uses, so the two lists sit on the same grid.
 *
 * The avatar carries no online dot on purpose. The contract projects a blocked user's profile to
 * its limited form, so there is no presence to show and drawing a grey dot would imply we know
 * they are offline.
 */
@Composable
private fun BlockedUserRow(
    blocked: BlockedUser,
    zone: TimeZone,
    onUnblock: () -> Unit,
) {
    val colors = SwetgramThemeTokens.colors

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .defaultMinSize(minHeight = TouchTarget.chatRowHeight)
            .padding(horizontal = Space.two, vertical = Space.one),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        SwetAvatar(
            displayName = blocked.user.displayName,
            size = Sizes.avatarMedium,
            surfaceColor = colors.background,
            avatarUrl = blocked.user.avatarUrl,
        )

        Spacer(Modifier.width(Space.two))

        Column(Modifier.weight(1f)) {
            Text(
                text = blocked.user.displayName,
                style = MaterialTheme.typography.titleMedium,
                color = colors.textPrimary,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )
            Spacer(Modifier.height(Space.half))
            Text(
                // The app's one spelling of a handle; see `ProfileScreen.ProfileIdentity`.
                text = stringResource(Res.string.handle_prefixed, blocked.user.username),
                style = MaterialTheme.typography.bodySmall,
                color = colors.textSecondary,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )
            // Omitted, not faked, when the date could not be read.
            blocked.blockedAt?.let {
                Spacer(Modifier.height(Space.half))
                Text(
                    text = stringResource(
                        Res.string.profile_blocked_on,
                        LocalDay.of(it, zone).render(),
                    ),
                    style = MaterialTheme.typography.labelSmall,
                    color = colors.textSecondary,
                    maxLines = 1,
                )
            }
        }

        Spacer(Modifier.width(Space.one))

        SwetTextButton(text = stringResource(Res.string.profile_unblock), onClick = onUnblock)
    }
}
